import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import type {Label} from './label'
import type {GraphQLResponse} from '@github-ui/item-picker-base/api/graphql'
import {buildItemPickerSearchUrl} from '@github-ui/item-picker-base/api/urls'
import {SearchLabels} from '@github-ui/server-persisted-queries'
import type {SearchLabelsQuery} from '@github-ui/server-persisted-queries/types/SearchLabels'

export type GetSearchLabelsRequest = {
  owner: string
  repo: string
  query?: string
  count?: number
  labelNames?: string[]
}

export type GetSearchLabelsResponse = GraphQLResponse<SearchLabelsQuery>

export async function apiGetSearchLabels({
  owner,
  repo,
  query,
  count,
  labelNames,
}: GetSearchLabelsRequest): Promise<GetSearchLabelsResponse> {
  const params: Record<string, unknown> = {owner, repo, queryId: SearchLabels}

  if (query) {
    params['query'] = query
  }

  if (count) {
    params['count'] = count
  }

  if (labelNames && labelNames.length > 0) {
    params['labelNames'] = labelNames.join(',')
  }

  const requestUrl = buildItemPickerSearchUrl('labels', params)
  const request = await verifiedFetchJSON(requestUrl)
  return (await request.json()) as GetSearchLabelsResponse
}

const itemPickerQueryKeyBase = 'item_picker'

export function buildItemPickerLabelSearchQueryKey(searchParams: GetSearchLabelsRequest) {
  return [itemPickerQueryKeyBase, 'labels', searchParams]
}

export type LabelPickerQueryData = {
  repositoryId: string
  labels: Label[]
  totalCount: number
  labelsByNames?: Label[]
}

/**
 * Helper function to strip quotes from label names and optionally filter labels by those names
 * @param labelNames Array of label names that may have quotes
 * @param labels Optional array of labels to filter
 * @returns Object with stripped names and optionally filtered labels
 */
export function processLabelNames(labelNames: string[], labels: Label[]) {
  const strippedNames = labelNames.map(name => name.replace(/^"/, '').replace(/"$/, ''))

  return labels.filter(label => strippedNames.includes(label.name))
}

export function transformSearchLabelResponseToQueryData(
  response: SearchLabelsQuery,
  labelNames?: string[],
): LabelPickerQueryData {
  const repository = response.repository
  const labels = repository?.labels

  if (!repository || !labels) {
    throw new Error('Invalid SearchLabels response: missing repository labels data')
  }

  const labelNodes = labels.nodes?.filter(label => label !== null) ?? []
  let matchedLabels: Label[] | undefined = repository.labelsByNames?.nodes?.filter(label => label !== null)

  // If labelNames were provided, filter to only those that match after stripping quotes
  if (labelNames && labelNames.length > 0 && matchedLabels) {
    matchedLabels = processLabelNames(labelNames, matchedLabels)
  }

  return {
    labels: labelNodes,
    repositoryId: repository.id,
    totalCount: labels.totalCount,
    labelsByNames: matchedLabels,
  }
}
